'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useMemo, useState } from 'react';
import { ExternLogoFull } from '@extern/ui/logos';
import Link from 'next/link';
import { LabsSurfaceProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/labs-surface';
import { PackProvider } from '@/app/(platform)/(subscription-gated)/labs/_engine/pack-context';
import { resolvePack } from '@/app/(platform)/(subscription-gated)/labs/_packs/registry';
import { getPreviewVisitorId } from '../_engine/anonymous-id';
import { readGuideSourceFromLocation } from '../_engine/guide-source';
import { PreviewAnalyticsProvider } from '../_engine/preview-analytics';
import { PreviewContainer } from '../_engine/preview-container';
import { toPreviewPack } from '../_engine/preview-copy';
import { PreviewFlow } from '../_engine/preview-flow';
import { PREVIEW_MODULE_COUNT, PREVIEW_STEPS, PREVIEW_STORE_VARIANT } from '../_engine/preview-steps';

/**
 * The public preview's client tree, and the one place its four providers are composed in order:
 *
 *   PackProvider              the vertical's content, plus a store bound to the SHORT step sequence
 *     LabsSurfaceProvider     two modules, no trial bar (the sprint's screens ask the host for both)
 *       PreviewContainer      resolves the anonymous visitor id, hydrates the draft, gates render
 *         PreviewAnalyticsProvider   arm-free tracking, which needs the visitor id
 *           PreviewFlow
 *
 * The analytics provider sits INSIDE the container on purpose. `labs_preview_viewed` is the
 * denominator for the whole funnel, and firing it before the visitor id resolved would leave the
 * fastest-bouncing readers carrying a null id. Those are the readers the number is most about.
 */

/** Height of the header, matching `theme(spacing.16)`, which is what `StepShell` subtracts from the viewport. */
const HEADER_CLASS = 'flex h-16 shrink-0 items-center justify-between border-fs-border border-b bg-fs-surface px-4';

/**
 * Takes the vertical SLUG rather than a pack, because a pack cannot cross the server-to-client
 * boundary: it carries `deriveArchetype` and a Lucide icon, and React serializes neither. The
 * registry is a plain module, so resolving here reaches exactly the same pack.
 */
export function PreviewApp(t0) {
  const $ = _c(14);
  const {
    vertical
  } = t0;
  const [visitorId] = useState(getPreviewVisitorId);
  const [guideSource] = useState(readGuideSourceFromLocation);
  let t1;
  if ($[0] !== vertical) {
    t1 = resolvePack(vertical);
    $[0] = vertical;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pack = t1;
  let t2;
  if ($[2] !== pack) {
    t2 = pack ? toPreviewPack(pack) : null;
    $[2] = pack;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const previewPack = t2;
  if (!previewPack) {
    return null;
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      steps: PREVIEW_STEPS,
      variant: PREVIEW_STORE_VARIANT
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      moduleCount: PREVIEW_MODULE_COUNT,
      trialBar: false
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <header className={HEADER_CLASS}><Link aria-label="Extern" href="https://extern.com"><ExternLogoFull className="h-6 w-auto" /></Link><Link className="rounded-fs-pill px-4 py-2 font-medium text-fs-body-md text-fs-brand hover:bg-fs-brand-subtle" href="/sign-in">Sign in</Link></header>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PreviewFlow />;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== guideSource || $[9] !== visitorId) {
    t7 = <LabsSurfaceProvider surface={t4}><div className="flex min-h-dvh flex-col bg-fs-canvas">{t5}<PreviewContainer><PreviewAnalyticsProvider guideSource={guideSource} visitorId={visitorId}>{t6}</PreviewAnalyticsProvider></PreviewContainer></div></LabsSurfaceProvider>;
    $[8] = guideSource;
    $[9] = visitorId;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== previewPack || $[12] !== t7) {
    t8 = <PackProvider pack={previewPack} storeOptions={t3}>{t7}</PackProvider>;
    $[11] = previewPack;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}